import { Heading, Text } from "../../../../components/common/typography";
import type { ProductRequestDto } from "../../../../api";
import { useBrand } from "../../../products/hooks/useBrand";
import { useCategory } from "../../../products/hooks/useCategory";
import { useProductWarranty } from "../../../products/hooks/useProductWarranty";
import { useProductWarrantyType } from "../../../products/hooks/useProductWarrantyType";
import { useCountry } from "../../../products/hooks/useCountry";
import { useProductDealType } from "../../../products/hooks/useProductDealType";
import { useAdminWarrantyProducts } from "../../hooks/useAdminWarrantyProducts";

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <Text variant="bodySm" tone="body">{label}</Text>
      <Text variant="body" tone="dark">{value}</Text>
    </div>
  );
}

interface ProductRequestDetailsInfoProps {
  request: ProductRequestDto;
}

export function ProductRequestDetailsInfo({
  request,
}: ProductRequestDetailsInfoProps) {
  const formatFallback = (id?: number | null) => (id != null ? `#${id}` : "—");

  const { data: brand } = useBrand(request.brandId ?? null);
  const { data: category } = useCategory(request.categoryId ?? null);
  const { data: warranty } = useProductWarranty(request.warrantyId ?? null);
  const { data: warrantyType } = useProductWarrantyType(
    request.warrantyTypeId ?? null
  );
  const { data: country } = useCountry(request.countryOfOriginId ?? null);
  const { data: dealType } = useProductDealType(request.dealTypeId ?? null);

  const compatibleIds = (request as ProductRequestDto & { compatibleWarrantyIds?: number[] }).compatibleWarrantyIds ?? [];
  const { data: allWarrantyProducts } = useAdminWarrantyProducts();
  const compatibleWarranties = compatibleIds.length > 0 && allWarrantyProducts
    ? allWarrantyProducts.filter((wp) => compatibleIds.includes(wp.id))
    : [];

  const brandName = brand?.name ?? formatFallback(request.brandId ?? undefined);
  const categoryName =
    category?.completeName ?? category?.name ?? formatFallback(request.categoryId ?? undefined);
  const warrantyName =
    warranty?.name ?? formatFallback(request.warrantyId ?? undefined);
  const warrantyTypeName =
    warrantyType?.name ?? formatFallback(request.warrantyTypeId ?? undefined);
  const countryName =
    country?.name ?? formatFallback(request.countryOfOriginId ?? undefined);
  const dealTypeName =
    dealType?.name ?? formatFallback(request.dealTypeId ?? undefined);

  const productTypeLabel =
    request.productType === "consu" ? "Consumable" : request.productType;

  const pairedRows: [{ label: string; value: string }, { label: string; value: string } | null][] = [
    [{ label: "Name", value: request.name }, { label: "Description", value: request.description || "—" }],
    [{ label: "Supplier ID", value: request.supplierId.toString() }, { label: "Brand", value: brandName }],
    [{ label: "Category", value: categoryName }, { label: "Product Type", value: productTypeLabel ?? "—" }],
    [{ label: "Warranty", value: warrantyName }, { label: "Warranty Type", value: warrantyTypeName }],
    [{ label: "Country of Origin", value: countryName }, { label: "Deal Type", value: dealTypeName }],
    [{ label: "Internal Notes", value: request.notes || "—" }, null],
  ];

  return (
    <div className="rounded-2xl border border-borderDefault bg-bgWhite shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)] w-full">
      <div className="px-6 pt-5 pb-4">
        <Heading variant="h3" tone="dark">Request Details</Heading>
      </div>
      <div className="border-t border-borderDefault divide-y divide-borderDefault px-6 pb-4">
        {pairedRows.map(([left, right]) => (
          <div key={left.label} className="grid grid-cols-2 gap-x-8 py-2">
            <Field label={left.label} value={left.value} />
            {right && <Field label={right.label} value={right.value} />}
          </div>
        ))}
        {compatibleWarranties.length > 0 && (
          <div className="flex flex-col gap-2 py-2">
            <Text variant="bodySm" tone="body">Extended Warranties</Text>
            <div className="flex flex-col gap-1.5">
              {compatibleWarranties.map((wp) => (
                <div key={wp.id} className="flex items-center justify-between rounded-xl border border-borderDefault px-4 py-2.5">
                  <Text variant="bodySm" tone="dark">{wp.name}</Text>
                  <Text variant="bodySmBold" tone="dark">
                    {wp.price != null && wp.price > 0 ? `+QAR ${wp.price.toLocaleString()}` : "Included"}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
